import type {
  FinancialPOAIntake,
  FinancialPOAPowers,
} from '@bedside-docs/contracts';

const POWER_LABELS: Record<keyof FinancialPOAPowers, string> = {
  realEstateTransactions: 'Real estate transactions',
  tangiblePersonalPropertyTransactions:
    'Tangible personal property transactions',
  bankingAndFinancialInstitutionTransactions:
    'Banking and financial institution transactions',
  businessOperatingTransactions: 'Business operating transactions',
  insuranceAndAnnuityTransactions: 'Insurance and annuity transactions',
  estateTrustAndBeneficiaryTransactions:
    'Estate, trust, and beneficiary transactions',
  claimsAndLitigation: 'Claims and litigation',
  personalAndFamilyMaintenance: 'Personal and family maintenance',
  retirementPlanTransactions: 'Retirement plan transactions',
  taxMatters: 'Tax matters',
};

const formatLine = (value: string) => value.trim();

const formatOptionalLine = (value?: string | null) => {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const formatAddressLines = (address: FinancialPOAIntake['principal']['address']) => {
  const line1 = formatLine(address.line1);
  const line2 = formatOptionalLine(address.line2);
  const city = formatLine(address.city);
  const state = formatLine(address.state).toUpperCase();
  const postalCode = formatLine(address.postalCode);

  const lines = [line1];

  if (line2) {
    lines.push(line2);
  }

  lines.push(`${city}, ${state} ${postalCode}`.trim());

  return lines;
};

export const formatDateOfBirth = (value?: string) => {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  return new Intl.DateTimeFormat(undefined, { dateStyle: 'long' }).format(date);
};

type PersonSummary = {
  name: string;
  dob?: string;
  phone: string;
  email?: string;
  addressLines: string[];
};

type SuccessorSummary = PersonSummary;

type PowersSummary = {
  granted: Array<{ key: keyof FinancialPOAPowers; label: string }>;
  effectivenessSummary: string;
  terminationConditions?: string;
  specialInstructions?: string;
};

type DeliverySummary = {
  sendToAgent: boolean;
  agentEmail?: string;
};

export type FinancialPOAReviewViewModel = {
  principal: PersonSummary;
  agent: PersonSummary & { successors: SuccessorSummary[] };
  powers: PowersSummary;
  delivery: DeliverySummary;
};

const summarizePerson = (
  contact: FinancialPOAIntake['principal'],
): PersonSummary => {
  return {
    name: formatLine(contact.fullName),
    dob: formatDateOfBirth(contact.dateOfBirth),
    phone: formatLine(contact.phone),
    email: formatOptionalLine(contact.email),
    addressLines: formatAddressLines(contact.address),
  };
};

const summarizeSuccessors = (
  successors: FinancialPOAIntake['successorAgents'],
): SuccessorSummary[] =>
  successors.map((successor) => ({
    name: formatLine(successor.fullName),
    dob: formatDateOfBirth(successor.dateOfBirth),
    phone: formatLine(successor.phone),
    email: formatOptionalLine(successor.email),
    addressLines: formatAddressLines(successor.address),
  }));

const summarizePowers = (
  intake: FinancialPOAIntake,
): PowersSummary => {
  const granted: Array<{ key: keyof FinancialPOAPowers; label: string }> = [];

  (Object.keys(POWER_LABELS) as Array<keyof FinancialPOAPowers>).forEach(
    (key) => {
      if (intake.powers[key]) {
        granted.push({ key, label: POWER_LABELS[key] });
      }
    },
  );

  const effectivenessSummary =
    intake.effectiveness.type === 'immediate'
      ? 'Takes effect immediately upon signing.'
      : 'Begins only if the principal becomes incapacitated.';

  return {
    granted,
    effectivenessSummary,
    terminationConditions: formatOptionalLine(
      intake.effectiveness.terminationConditions,
    ),
    specialInstructions: formatOptionalLine(intake.specialInstructions),
  };
};

const summarizeDelivery = (intake: FinancialPOAIntake): DeliverySummary => {
  if (!intake.delivery) {
    return { sendToAgent: false };
  }

  return {
    sendToAgent: Boolean(intake.delivery.sendToAgent),
    agentEmail: formatOptionalLine(intake.delivery.agentEmail),
  };
};

export const toFinancialPOAReviewViewModel = (
  intake: FinancialPOAIntake,
): FinancialPOAReviewViewModel => {
  const principal = summarizePerson(intake.principal);
  const agent = {
    ...summarizePerson(intake.agent),
    successors: summarizeSuccessors(intake.successorAgents),
  };

  return {
    principal,
    agent,
    powers: summarizePowers(intake),
    delivery: summarizeDelivery(intake),
  };
};
